import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = 'https://fitpulse.es/api';
const fmt = (d: string) => new Date(d).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Madrid' });
const fmtT = (d: string) => new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' });
const fmtShort = (d: string) => new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' });

export function PublicBusiness() {
  const { slug } = useParams<{ slug: string }>();
  const [biz, setBiz] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'eventos' | 'resenas' | 'info'>('eventos');
  const [filter, setFilter] = useState('todos');

  // Auth
  const [token, setToken] = useState(localStorage.getItem('accessToken') || '');
  const [userEmail, setUserEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Booking
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [numPersonas, setNumPersonas] = useState(1);
  const [sending, setSending] = useState(false);
  const [bookingDone, setBookingDone] = useState(false);
  const [inscripcionHecha, setInscripcionHecha] = useState(false);

  // Auth form
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  // Reviews
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');

  useEffect(() => { loadBiz(); checkAuth(); }, [slug]);

  const checkAuth = async () => {
    const t = localStorage.getItem('accessToken');
    if (!t) return;
    try {
      const r = await axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${t}` } });
      setToken(t); setUserEmail(r.data.data.user.email); setIsLoggedIn(true);
    } catch { localStorage.removeItem('accessToken'); }
  };

  const loadBiz = async () => {
    try {
      const bRes = await axios.get(`${API}/businesses/public/${slug}`);
      const business = bRes.data.data.business;
      setBiz(business);
      const [eRes, rRes] = await Promise.all([
        axios.get(`${API}/events/public/${business.id}`).catch(() => ({ data: { data: { events: [] } } })),
        axios.get(`${API}/reviews/${business.id}`).catch(() => ({ data: { data: { reviews: [] } } })),
      ]);
      setEvents(eRes.data.data?.events || []);
      setReviews(rRes.data.data?.reviews || []);
    } catch { setBiz(null); }
    finally { setLoading(false); }
  };

  const handleAuth = async () => {
    if (!email || !password) { toast.error('Rellena email y contraseña'); return; }
    setAuthLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const body = authMode === 'login' ? { email, password } : { email, password, firstName, lastName, phone, role: 'CLIENT' };
      const r = await axios.post(`${API}${endpoint}`, body);
      const { accessToken } = r.data.data;
      localStorage.setItem('accessToken', accessToken);
      setToken(accessToken); setUserEmail(email); setIsLoggedIn(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error al autenticar');
    } finally { setAuthLoading(false); }
  };

  const confirmarInscripcion = async () => {
    if (!selectedEvent || sending) return;
    const activeToken = localStorage.getItem('accessToken') || token;
    if (!activeToken) { toast.error('Debes iniciar sesión'); return; }
    const np = Math.max(1, numPersonas);
    setSending(true);
    try {
      const isFree = !selectedEvent.price || Number(selectedEvent.price) === 0;
      if (isFree) {
        await axios.post(`${API}/event-bookings/${selectedEvent.id}`, { numPersonas: np }, { headers: { Authorization: `Bearer ${activeToken}` } });
      } else {
        try {
          const r = await axios.post(`${API}/event-payments/checkout/${selectedEvent.id}`,
            { successUrl: `${window.location.href}?paid=true`, cancelUrl: window.location.href },
            { headers: { Authorization: `Bearer ${activeToken}` } }
          );
          window.location.href = r.data.data.checkoutUrl;
          return;
        } catch (e: any) {
          const msg = e?.response?.data?.message || '';
          if (msg.includes('Stripe') || msg.includes('pagos')) {
            await axios.post(`${API}/event-bookings/${selectedEvent.id}`, { numPersonas: np }, { headers: { Authorization: `Bearer ${activeToken}` } });
          } else throw e;
        }
      }
      setInscripcionHecha(true);
      setTimeout(() => {
        setSelectedEvent(null);
        setNumPersonas(1);
        setInscripcionHecha(false);
        setBookingDone(true);
        setTimeout(() => setBookingDone(false), 8000);
        loadBiz();
      }, 2500);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Error al inscribirse';
      toast.error(msg);
      if (msg.includes('Ya estás inscrito')) { setSelectedEvent(null); setNumPersonas(1); }
    } finally { setSending(false); }
  };

  const handleSubmitReview = async () => {
    try {
      await axios.post(`${API}/reviews/${biz.id}`, { rating: reviewRating, comment: reviewComment }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('✅ Reseña publicada');
      setShowReviewForm(false); setReviewComment(''); loadBiz();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Error'); }
  };

  const now = new Date();
  const filteredEvents = events.filter(e => {
    const start = new Date(e.startTime);
    if (filter === 'hoy') return start.toDateString() === now.toDateString();
    if (filter === 'semana') { const w = new Date(now); w.setDate(w.getDate()+7); return start >= now && start <= w; }
    if (filter === 'gratis') return !e.price || Number(e.price) === 0;
    if (filter === 'pago') return e.price && Number(e.price) > 0;
    return true;
  });

  const reviewAvg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const upcomingEvents = events.filter(e => new Date(e.startTime) >= now);

  if (loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#f8fafc'}}>
      <div style={{textAlign:'center'}}>
        <div style={{width:44,height:44,border:'3px solid #e2e8f0',borderTop:'3px solid #2563eb',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 16px'}}></div>
        <p style={{color:'#64748b',fontSize:'.9rem'}}>Cargando...</p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!biz) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16,background:'#f8fafc'}}>
      <div style={{fontSize:'3rem'}}>🔍</div>
      <p style={{color:'#1e293b',fontWeight:700,fontSize:'1.1rem'}}>Negocio no encontrado</p>
      <a href="https://fitpulse.es" style={{color:'#2563eb',fontWeight:600,textDecoration:'none'}}>← Volver a FitPulse</a>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#f8fafc',fontFamily:"'Inter',sans-serif"}}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Inter',sans-serif}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideDown{from{opacity:0;transform:translateX(-50%) translateY(-12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
        .pb-nav{position:fixed;top:0;left:0;right:0;height:60px;background:rgba(255,255,255,.95);backdrop-filter:blur(12px);border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;padding:0 24px;z-index:50;box-shadow:0 1px 8px rgba(0,0,0,.06)}
        .pb-cover{height:320px;position:relative;overflow:hidden;background:linear-gradient(135deg,#1e293b 0%,#334155 100%);margin-top:60px}
        .pb-cover img{width:100%;height:100%;object-fit:cover}
        .pb-cover-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.6) 0%,rgba(0,0,0,.1) 60%,transparent 100%)}
        .pb-main{max-width:1100px;margin:0 auto;padding:0 24px}
        .pb-profile{display:flex;gap:20px;align-items:flex-end;margin-top:-52px;margin-bottom:24px;position:relative;z-index:2}
        .pb-avatar{width:96px;height:96px;border-radius:20px;border:4px solid white;background:linear-gradient(135deg,#2563eb,#7c3aed);display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:2.2rem;overflow:hidden;flex-shrink:0;box-shadow:0 8px 24px rgba(0,0,0,.2)}
        .pb-info{flex:1;padding-bottom:8px}
        .pb-name{font-size:1.7rem;font-weight:900;color:white;text-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;gap:10px;margin-bottom:4px}
        .pb-meta{display:flex;gap:14px;flex-wrap:wrap;font-size:.82rem}
        .pb-meta span{color:rgba(255,255,255,.85);display:flex;align-items:center;gap:4px}
        .pb-content{display:grid;grid-template-columns:1fr 340px;gap:28px;align-items:start}
        @media(max-width:900px){.pb-content{grid-template-columns:1fr}.pb-sidebar{display:none}}
        .pb-tabs{display:flex;gap:0;border-bottom:2px solid #e2e8f0;margin-bottom:24px}
        .pb-tab{padding:12px 20px;border:none;background:none;cursor:pointer;font-weight:600;font-size:.88rem;color:#64748b;border-bottom:2px solid transparent;margin-bottom:-2px;font-family:inherit;transition:all .2s}
        .pb-tab.active{color:#2563eb;border-bottom-color:#2563eb}
        .filter-wrap{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px}
        .filter-btn{padding:7px 16px;border-radius:100px;border:1.5px solid #e2e8f0;background:white;font-size:.78rem;font-weight:600;cursor:pointer;font-family:inherit;color:#64748b;transition:all .2s;white-space:nowrap}
        .filter-btn.active{background:#2563eb;border-color:#2563eb;color:white;box-shadow:0 2px 8px rgba(37,99,235,.3)}
        .ev-card{background:white;border-radius:16px;overflow:hidden;border:1px solid #e2e8f0;transition:all .25s;margin-bottom:14px;display:flex;cursor:pointer}
        .ev-card:hover{border-color:#2563eb;box-shadow:0 6px 24px rgba(37,99,235,.12);transform:translateY(-2px)}
        .ev-img{width:140px;min-height:140px;flex-shrink:0;background:linear-gradient(135deg,#1e293b,#334155);position:relative;overflow:hidden}
        .ev-img img{width:100%;height:100%;object-fit:cover}
        .ev-date-badge{position:absolute;top:10px;left:10px;background:white;border-radius:10px;padding:6px 10px;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.15)}
        .ev-date-day{font-size:1.2rem;font-weight:900;color:#1e293b;line-height:1}
        .ev-date-mon{font-size:.6rem;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.06em}
        .ev-body{flex:1;padding:16px 18px;display:flex;flex-direction:column;justify-content:space-between}
        .ev-title{font-size:1rem;font-weight:800;color:#1e293b;margin-bottom:6px}
        .ev-details{display:flex;gap:12px;font-size:.75rem;color:#64748b;flex-wrap:wrap;margin-bottom:10px}
        .ev-footer{display:flex;align-items:center;justify-content:space-between}
        .ev-price{font-weight:800;font-size:.95rem}
        .ev-spots{font-size:.72rem;font-weight:600;padding:4px 10px;border-radius:100px}
        .spots-ok{background:#dcfce7;color:#15803d}
        .spots-low{background:#fef9c3;color:#854d0e}
        .spots-full{background:#fee2e2;color:#dc2626}
        .btn-book{width:100%;padding:14px;border-radius:12px;border:none;background:#2563eb;color:white;font-weight:700;font-size:.92rem;cursor:pointer;font-family:inherit;transition:all .2s;margin-top:10px}
        .btn-book:hover:not(:disabled){background:#1d4ed8;transform:translateY(-1px);box-shadow:0 4px 12px rgba(37,99,235,.35)}
        .btn-book:disabled{background:#93c5fd;cursor:not-allowed}
        .sidebar-card{background:white;border-radius:16px;border:1px solid #e2e8f0;overflow:hidden;position:sticky;top:80px}
        .sidebar-header{padding:20px;border-bottom:1px solid #f1f5f9}
        .sidebar-body{padding:20px}
        .info-row{display:flex;gap:10px;align-items:center;padding:10px 0;border-bottom:1px solid #f8fafc;font-size:.85rem;color:#475569}
        .modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:200;display:flex;align-items:flex-end;justify-content:center}
        @media(min-width:640px){.modal-bg{align-items:center;padding:20px}}
        .modal-box{background:white;border-radius:24px 24px 0 0;padding:28px 24px;width:100%;max-width:460px;max-height:92vh;overflow-y:auto;animation:fadeUp .25s ease}
        @media(min-width:640px){.modal-box{border-radius:24px}}
        .modal-ev-header{display:flex;gap:12px;align-items:center;padding:14px;background:#f8fafc;border-radius:12px;margin-bottom:20px}
        .modal-ev-img{width:56px;height:56px;border-radius:10px;background:linear-gradient(135deg,#1e293b,#334155);overflow:hidden;flex-shrink:0}
        .modal-ev-img img{width:100%;height:100%;object-fit:cover}
        .num-ctrl{display:flex;align-items:center;border:1.5px solid #e2e8f0;border-radius:12px;overflow:hidden}
        .num-btn{width:44px;height:44px;border:none;background:#f8fafc;cursor:pointer;font-size:1.2rem;font-weight:700;color:#1e293b;transition:background .15s;flex-shrink:0}
        .num-btn:hover{background:#e2e8f0}
        .num-val{flex:1;text-align:center;font-size:1.1rem;font-weight:800;color:#1e293b}
        .auth-tabs{display:flex;background:#f1f5f9;border-radius:10px;padding:4px;gap:4px;margin-bottom:14px}
        .auth-tab{flex:1;padding:8px;border:none;border-radius:8px;font-weight:600;font-size:.82rem;cursor:pointer;font-family:inherit;transition:all .2s}
        .fi{width:100%;padding:11px 14px;border:1.5px solid #e2e8f0;border-radius:10px;font-size:.88rem;font-family:inherit;outline:none;margin-bottom:8px;color:#1e293b}
        .fi:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.1)}
        .success-toast{position:fixed;top:80px;left:50%;transform:translateX(-50%);background:#10b981;color:white;border-radius:14px;padding:18px 28px;z-index:300;text-align:center;box-shadow:0 8px 32px rgba(0,0,0,.2);min-width:280px;animation:slideDown .3s ease}
        .review-card{background:white;border-radius:12px;border:1px solid #e2e8f0;padding:16px;margin-bottom:10px}
        .social-btn{width:32px;height:32px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;text-decoration:none;transition:transform .2s;flex-shrink:0}
        .social-btn:hover{transform:scale(1.1)}
      `}</style>

      {/* NAV */}
      <nav className="pb-nav">
        <a href="https://fitpulse.es" style={{fontWeight:900,color:'#2563eb',textDecoration:'none',fontSize:'1rem',display:'flex',alignItems:'center',gap:6}}>
          <div style={{width:8,height:8,background:'#2563eb',borderRadius:'50%'}}></div>FitPulse
        </a>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {isLoggedIn ? (
            <>
              <a href="https://admin.fitpulse.es/mis-inscripciones" style={{fontSize:'.78rem',fontWeight:600,color:'#2563eb',textDecoration:'none',padding:'7px 14px',border:'1px solid #bfdbfe',borderRadius:8,background:'#eff6ff'}}>
                Mis inscripciones
              </a>
              <button onClick={() => { localStorage.removeItem('accessToken'); setIsLoggedIn(false); setToken(''); setUserEmail(''); }}
                style={{background:'none',border:'1px solid #e2e8f0',borderRadius:8,padding:'7px 12px',cursor:'pointer',fontSize:'.75rem',color:'#64748b',fontFamily:'inherit'}}>
                Salir
              </button>
            </>
          ) : (
            <>
              <a href="https://admin.fitpulse.es/login" style={{fontSize:'.82rem',fontWeight:600,color:'#64748b',textDecoration:'none'}}>Iniciar sesión</a>
              <a href="https://admin.fitpulse.es/register" style={{background:'#2563eb',color:'white',padding:'8px 16px',borderRadius:9,fontSize:'.82rem',fontWeight:700,textDecoration:'none',boxShadow:'0 2px 8px rgba(37,99,235,.3)'}}>
                Registrarse
              </a>
            </>
          )}
        </div>
      </nav>

      {/* SUCCESS TOAST */}
      {bookingDone && (
        <div className="success-toast">
          <div style={{fontSize:'1.8rem',marginBottom:8}}>🎉</div>
          <p style={{fontWeight:800,fontSize:'1rem',marginBottom:4}}>¡Inscripción confirmada!</p>
          <p style={{fontSize:'.8rem',opacity:.9}}>Recibirás un email con tu código QR</p>
          <button onClick={() => setBookingDone(false)} style={{marginTop:12,background:'rgba(255,255,255,.2)',border:'none',color:'white',padding:'6px 16px',borderRadius:8,cursor:'pointer',fontFamily:'inherit',fontWeight:600,fontSize:'.78rem'}}>
            ✓ Cerrar
          </button>
        </div>
      )}

      {/* COVER */}
      <div className="pb-cover">
        {biz.coverImage ? <img src={biz.coverImage} alt={biz.name}/> : (
          <div style={{width:'100%',height:'100%',background:`linear-gradient(135deg, #1e293b, #0f172a)`,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{fontSize:'6rem',opacity:.08}}>🏢</div>
          </div>
        )}
        <div className="pb-cover-overlay"/>
        {/* Nombre sobre la portada */}
        <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'0 24px 20px'}}>
          <div style={{maxWidth:1100,margin:'0 auto'}}>
            <div className="pb-name">
              {biz.name.charAt(0).toUpperCase()+biz.name.slice(1)}
              {biz.badge && (
                <span style={{width:26,height:26,borderRadius:'50%',background:biz.badge==='premium'?'#f59e0b':biz.badge==='pro'?'#6366f1':'#2563eb',display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:'0 2px 8px rgba(0,0,0,.3)'}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              )}
            </div>
            <div className="pb-meta">
              {biz.city && <span>📍 {biz.city}</span>}
              {biz.category && <span>🏷️ {biz.category}</span>}
              {reviewAvg > 0 && <span>⭐ {reviewAvg.toFixed(1)} ({reviews.length} reseñas)</span>}
              <span>📅 {upcomingEvents.length} evento{upcomingEvents.length !== 1 ? 's' : ''} próximo{upcomingEvents.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="pb-main" style={{paddingTop:24,paddingBottom:48}}>
        {/* Avatar y redes sociales */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:28,flexWrap:'wrap',gap:12}}>
          <div style={{display:'flex',gap:14,alignItems:'center'}}>
            <div className="pb-avatar">
              {biz.logo ? <img src={biz.logo} alt={biz.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/> : biz.name.charAt(0).toUpperCase()}
            </div>
            {biz.description && <p style={{fontSize:'.88rem',color:'#475569',maxWidth:500,lineHeight:1.6}}>{biz.description}</p>}
          </div>
          {biz.badge && (
            <div style={{display:'flex',gap:8}}>
              {biz.whatsapp && <a href={`https://wa.me/${biz.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="social-btn" style={{background:'#25D366'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>}
              {biz.instagram && <a href={`https://instagram.com/${biz.instagram}`} target="_blank" rel="noreferrer" className="social-btn" style={{background:'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>}
              {biz.facebook && <a href={biz.facebook} target="_blank" rel="noreferrer" className="social-btn" style={{background:'#1877F2'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>}
              {biz.tiktok && <a href={`https://tiktok.com/@${biz.tiktok}`} target="_blank" rel="noreferrer" className="social-btn" style={{background:'#000'}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.19 8.19 0 004.79 1.53V6.78a4.85 4.85 0 01-1.02-.09z"/></svg>
              </a>}
            </div>
          )}
        </div>

        <div className="pb-content">
          {/* LEFT */}
          <div>
            {/* TABS */}
            <div className="pb-tabs">
              <button className={`pb-tab ${tab==='eventos'?'active':''}`} onClick={() => setTab('eventos')}>
                📅 Eventos ({upcomingEvents.length})
              </button>
              <button className={`pb-tab ${tab==='resenas'?'active':''}`} onClick={() => setTab('resenas')}>
                ⭐ Reseñas ({reviews.length})
              </button>
              <button className={`pb-tab ${tab==='info'?'active':''}`} onClick={() => setTab('info')}>
                ℹ️ Info
              </button>
            </div>

            {/* EVENTOS */}
            {tab === 'eventos' && (
              <>
                <div className="filter-wrap">
                  {['todos','hoy','semana','gratis','pago'].map(f => (
                    <button key={f} className={`filter-btn ${filter===f?'active':''}`} onClick={() => setFilter(f)}>
                      {f==='todos'?'📅 Todos':f==='hoy'?'⚡ Hoy':f==='semana'?'📆 Esta semana':f==='gratis'?'🆓 Gratis':'💶 De pago'}
                    </button>
                  ))}
                </div>

                {filteredEvents.length === 0 ? (
                  <div style={{textAlign:'center',padding:'48px',background:'white',borderRadius:16,border:'1px solid #e2e8f0'}}>
                    <div style={{fontSize:'2.5rem',marginBottom:12}}>📅</div>
                    <p style={{fontWeight:700,color:'#1e293b',marginBottom:6}}>Sin eventos disponibles</p>
                    <p style={{color:'#64748b',fontSize:'.85rem'}}>Prueba con otros filtros</p>
                  </div>
                ) : filteredEvents.map(ev => {
                  const d = new Date(ev.startTime);
                  const spots = ev.maxCapacity - (ev._count?.bookings || 0);
                  const isFree = !ev.price || Number(ev.price) === 0;
                  const isPast = d < now;
                  return (
                    <div key={ev.id} className="ev-card" onClick={() => !isPast && spots > 0 && (setSelectedEvent(ev), setNumPersonas(1))}>
                      <div className="ev-img">
                        {ev.image ? <img src={ev.image} alt={ev.title}/> : <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'2.5rem',opacity:.15}}>📅</div>}
                        <div className="ev-date-badge">
                          <div className="ev-date-day">{d.getDate()}</div>
                          <div className="ev-date-mon">{d.toLocaleDateString('es-ES',{month:'short',timeZone:'Europe/Madrid'})}</div>
                        </div>
                      </div>
                      <div className="ev-body">
                        <div>
                          <div style={{display:'flex',gap:6,marginBottom:6,flexWrap:'wrap'}}>
                            <span style={{background:isFree?'#dcfce7':'#dbeafe',color:isFree?'#15803d':'#1d4ed8',fontSize:'.68rem',fontWeight:700,padding:'3px 8px',borderRadius:100}}>
                              {isFree ? '🆓 Gratis' : `💶 ${ev.price}€`}
                            </span>
                            {isPast && <span style={{background:'#f1f5f9',color:'#64748b',fontSize:'.68rem',fontWeight:700,padding:'3px 8px',borderRadius:100}}>Pasado</span>}
                            {spots <= 0 && !isPast && <span style={{background:'#fee2e2',color:'#dc2626',fontSize:'.68rem',fontWeight:700,padding:'3px 8px',borderRadius:100}}>Completo</span>}
                          </div>
                          <div className="ev-title">{ev.title}</div>
                          <div className="ev-details">
                            <span>🕐 {fmtT(ev.startTime)}</span>
                            {ev.location && <span>📍 {ev.location}</span>}
                          </div>
                          {ev.description && <p style={{fontSize:'.78rem',color:'#64748b',lineHeight:1.5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{ev.description}</p>}
                        </div>
                        <div className="ev-footer">
                          <span className={`ev-spots ${spots <= 0 ? 'spots-full' : spots <= 5 ? 'spots-low' : 'spots-ok'}`}>
                            {spots <= 0 ? 'Sin plazas' : spots <= 5 ? `⚡ ${spots} plazas` : `👥 ${ev._count?.bookings||0}/${ev.maxCapacity}`}
                          </span>
                          {!isPast && spots > 0 && (
                            <span style={{fontSize:'.78rem',fontWeight:700,color:'#2563eb'}}>Ver más →</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* RESEÑAS */}
            {tab === 'resenas' && (
              <>
                {reviewAvg > 0 && (
                  <div style={{background:'white',borderRadius:16,border:'1px solid #e2e8f0',padding:24,marginBottom:20,display:'flex',gap:24,alignItems:'center'}}>
                    <div style={{textAlign:'center',flexShrink:0}}>
                      <div style={{fontSize:'3rem',fontWeight:900,color:'#1e293b',lineHeight:1}}>{reviewAvg.toFixed(1)}</div>
                      <div style={{display:'flex',gap:2,justifyContent:'center',margin:'6px 0'}}>
                        {[1,2,3,4,5].map(s => <span key={s} style={{fontSize:'1.1rem',color:s<=Math.round(reviewAvg)?'#f59e0b':'#e2e8f0'}}>★</span>)}
                      </div>
                      <div style={{fontSize:'.75rem',color:'#64748b'}}>{reviews.length} reseñas</div>
                    </div>
                    <div style={{flex:1}}>
                      {[5,4,3,2,1].map(s => {
                        const count = reviews.filter(r => r.rating === s).length;
                        const pct = reviews.length ? (count/reviews.length)*100 : 0;
                        return (
                          <div key={s} style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                            <span style={{fontSize:'.72rem',color:'#64748b',width:12}}>{s}</span>
                            <div style={{flex:1,height:6,background:'#f1f5f9',borderRadius:100,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${pct}%`,background:'#f59e0b',borderRadius:100}}></div>
                            </div>
                            <span style={{fontSize:'.72rem',color:'#94a3b8',width:20}}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isLoggedIn && (
                  <div style={{marginBottom:16}}>
                    {!showReviewForm ? (
                      <button onClick={() => setShowReviewForm(true)} style={{background:'#2563eb',color:'white',border:'none',padding:'10px 20px',borderRadius:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit',fontSize:'.85rem'}}>
                        ✍️ Escribir reseña
                      </button>
                    ) : (
                      <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:14,padding:20}}>
                        <p style={{fontWeight:700,fontSize:'.9rem',color:'#1e293b',marginBottom:12}}>Tu valoración</p>
                        <div style={{display:'flex',gap:6,marginBottom:14}}>
                          {[1,2,3,4,5].map(s => (
                            <button key={s} onClick={() => setReviewRating(s)} style={{fontSize:'2rem',background:'none',border:'none',cursor:'pointer',color:s<=reviewRating?'#f59e0b':'#e2e8f0',padding:0,transition:'color .15s'}}>★</button>
                          ))}
                        </div>
                        <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Cuéntanos tu experiencia..."
                          style={{width:'100%',padding:'11px 14px',border:'1.5px solid #e2e8f0',borderRadius:10,fontSize:'.85rem',fontFamily:'inherit',resize:'none',outline:'none',marginBottom:10}} rows={3}/>
                        <div style={{display:'flex',gap:8}}>
                          <button onClick={() => setShowReviewForm(false)} style={{padding:'9px 16px',borderRadius:9,border:'1px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.82rem',cursor:'pointer',fontFamily:'inherit',color:'#64748b'}}>Cancelar</button>
                          <button onClick={handleSubmitReview} style={{padding:'9px 20px',borderRadius:9,border:'none',background:'#2563eb',color:'white',fontWeight:700,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit'}}>✅ Publicar</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {reviews.map((r, i) => (
                  <div key={i} className="review-card">
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                      <div style={{display:'flex',gap:10,alignItems:'center'}}>
                        <div style={{width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,#2563eb,#7c3aed)',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:700,fontSize:'.82rem',flexShrink:0}}>
                          {r.user?.firstName?.charAt(0)}{r.user?.lastName?.charAt(0)}
                        </div>
                        <div>
                          <p style={{fontWeight:700,fontSize:'.88rem',color:'#1e293b'}}>{r.user?.firstName} {r.user?.lastName}</p>
                          <p style={{fontSize:'.72rem',color:'#94a3b8'}}>{new Date(r.createdAt).toLocaleDateString('es-ES')}</p>
                        </div>
                      </div>
                      <div style={{color:'#f59e0b',fontSize:'.9rem'}}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div>
                    </div>
                    {r.comment && <p style={{fontSize:'.83rem',color:'#475569',lineHeight:1.6}}>{r.comment}</p>}
                  </div>
                ))}

                {reviews.length === 0 && (
                  <div style={{textAlign:'center',padding:'40px',background:'white',borderRadius:14,border:'1px solid #e2e8f0'}}>
                    <div style={{fontSize:'2rem',marginBottom:10}}>⭐</div>
                    <p style={{color:'#64748b',fontWeight:600}}>Sin reseñas todavía</p>
                    <p style={{color:'#94a3b8',fontSize:'.82rem',marginTop:4}}>Sé el primero en valorar este negocio</p>
                  </div>
                )}
              </>
            )}

            {/* INFO */}
            {tab === 'info' && (
              <div style={{background:'white',borderRadius:16,border:'1px solid #e2e8f0',overflow:'hidden'}}>
                <div style={{padding:'20px 24px',borderBottom:'1px solid #f1f5f9',fontWeight:700,fontSize:'.95rem',color:'#1e293b'}}>Información del negocio</div>
                <div style={{padding:'8px 24px 16px'}}>
                  {biz.city && <div className="info-row"><span>📍</span><span>{biz.city}{biz.address ? `, ${biz.address}` : ''}</span></div>}
                  {biz.phone && <div className="info-row"><span>📞</span><a href={`tel:${biz.phone}`} style={{color:'#2563eb',textDecoration:'none'}}>{biz.phone}</a></div>}
                  {biz.email && <div className="info-row"><span>✉️</span><a href={`mailto:${biz.email}`} style={{color:'#2563eb',textDecoration:'none'}}>{biz.email}</a></div>}
                  {biz.website && <div className="info-row"><span>🌐</span><a href={biz.website} target="_blank" rel="noreferrer" style={{color:'#2563eb',textDecoration:'none'}}>{biz.website}</a></div>}
                  {biz.category && <div className="info-row"><span>🏷️</span><span>{biz.category}</span></div>}
                  {biz.description && <div style={{paddingTop:14,fontSize:'.85rem',color:'#475569',lineHeight:1.7}}>{biz.description}</div>}
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <div className="pb-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-header">
                <p style={{fontWeight:700,fontSize:'.95rem',color:'#1e293b',marginBottom:4}}>Próximos eventos</p>
                <p style={{fontSize:'.78rem',color:'#64748b'}}>{upcomingEvents.length} evento{upcomingEvents.length!==1?'s':''} disponible{upcomingEvents.length!==1?'s':''}</p>
              </div>
              <div className="sidebar-body" style={{padding:'12px 16px'}}>
                {upcomingEvents.slice(0,4).map(ev => {
                  const spots = ev.maxCapacity - (ev._count?.bookings || 0);
                  const isFree = !ev.price || Number(ev.price) === 0;
                  return (
                    <div key={ev.id} onClick={() => spots > 0 && (setSelectedEvent(ev), setNumPersonas(1))}
                      style={{display:'flex',gap:10,alignItems:'center',padding:'10px',borderRadius:10,cursor:spots>0?'pointer':'default',transition:'background .15s',marginBottom:6}}
                      onMouseOver={e => spots>0 && ((e.currentTarget as any).style.background='#f8fafc')}
                      onMouseOut={e => (e.currentTarget as any).style.background='transparent'}>
                      <div style={{width:40,height:40,background:'linear-gradient(135deg,#2563eb,#7c3aed)',borderRadius:10,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'white',flexShrink:0}}>
                        <span style={{fontSize:'.9rem',fontWeight:900,lineHeight:1}}>{new Date(ev.startTime).getDate()}</span>
                        <span style={{fontSize:'.5rem',fontWeight:700,textTransform:'uppercase',opacity:.8}}>{new Date(ev.startTime).toLocaleDateString('es-ES',{month:'short',timeZone:'Europe/Madrid'})}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{fontWeight:700,fontSize:'.82rem',color:'#1e293b',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ev.title}</p>
                        <p style={{fontSize:'.7rem',color:'#64748b'}}>{fmtT(ev.startTime)} · {isFree?'Gratis':`${ev.price}€`}</p>
                      </div>
                      <span style={{fontSize:'.68rem',fontWeight:700,color:spots<=0?'#dc2626':spots<=5?'#f59e0b':'#15803d',flexShrink:0}}>{spots<=0?'Lleno':spots<=5?`${spots}pl`:''}</span>
                    </div>
                  );
                })}
                {upcomingEvents.length === 0 && <p style={{color:'#94a3b8',fontSize:'.82rem',textAlign:'center',padding:'16px 0'}}>Sin eventos próximos</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL INSCRIPCIÓN */}
      {selectedEvent && (
        <div className="modal-bg" onClick={() => !sending && !inscripcionHecha && setSelectedEvent(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            {inscripcionHecha ? (
              <div style={{textAlign:'center',padding:'20px 0',animation:'fadeUp .3s ease'}}>
                <div style={{fontSize:'3.5rem',marginBottom:14}}>🎉</div>
                <p style={{fontWeight:900,fontSize:'1.2rem',color:'#15803d',marginBottom:8}}>¡Inscripción confirmada!</p>
                <p style={{fontSize:'.88rem',color:'#64748b',marginBottom:16,lineHeight:1.5}}>Recibirás un email con todos los detalles y tu código QR de entrada.</p>
                <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:12,padding:'12px 16px',fontSize:'.82rem',color:'#15803d',fontWeight:600}}>
                  Cerrando automáticamente...
                </div>
              </div>
            ) : (
              <>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
                  <h2 style={{fontWeight:800,fontSize:'1.05rem',color:'#1e293b'}}>Reservar plaza</h2>
                  <button onClick={() => setSelectedEvent(null)} style={{background:'#f1f5f9',border:'none',cursor:'pointer',color:'#64748b',borderRadius:'50%',width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem'}}>✕</button>
                </div>

                {/* Evento info */}
                <div className="modal-ev-header">
                  <div className="modal-ev-img">
                    {selectedEvent.image ? <img src={selectedEvent.image} alt={selectedEvent.title}/> : <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.5rem',opacity:.3}}>📅</div>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontWeight:800,fontSize:'.92rem',color:'#1e293b',marginBottom:4}}>{selectedEvent.title}</p>
                    <p style={{fontSize:'.75rem',color:'#64748b'}}>📅 {fmt(selectedEvent.startTime)}</p>
                    <p style={{fontSize:'.75rem',color:'#64748b'}}>🕐 {fmtT(selectedEvent.startTime)} – {fmtT(selectedEvent.endTime)}</p>
                    {selectedEvent.location && <p style={{fontSize:'.75rem',color:'#64748b'}}>📍 {selectedEvent.location}</p>}
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <p style={{fontWeight:900,fontSize:'1.1rem',color:selectedEvent.price&&Number(selectedEvent.price)>0?'#2563eb':'#10b981'}}>
                      {selectedEvent.price&&Number(selectedEvent.price)>0?`${selectedEvent.price}€`:'Gratis'}
                    </p>
                    <p style={{fontSize:'.68rem',color:'#94a3b8'}}>por persona</p>
                  </div>
                </div>

                {/* Número de personas */}
                <div style={{marginBottom:18}}>
                  <p style={{fontSize:'.82rem',fontWeight:700,color:'#475569',marginBottom:10}}>👥 Número de personas</p>
                  <div className="num-ctrl">
                    <button className="num-btn" onClick={() => setNumPersonas(Math.max(1,numPersonas-1))}>−</button>
                    <div className="num-val">{numPersonas}</div>
                    <button className="num-btn" onClick={() => setNumPersonas(Math.min(10,numPersonas+1))}>+</button>
                  </div>
                  {selectedEvent.price && Number(selectedEvent.price) > 0 && numPersonas > 1 && (
                    <div style={{marginTop:10,background:'#eff6ff',borderRadius:10,padding:'10px 14px',display:'flex',justifyContent:'space-between',fontSize:'.85rem',color:'#1d4ed8',fontWeight:600}}>
                      <span>Total ({numPersonas} personas)</span>
                      <span style={{fontWeight:800}}>{(Number(selectedEvent.price)*numPersonas).toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                {/* Auth o confirmar */}
                {isLoggedIn ? (
                  <>
                    <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.80rem',color:'#15803d',display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:'.9rem'}}>✅</span>
                      <span>Conectado como <strong>{userEmail}</strong></span>
                      <button onClick={() => { localStorage.removeItem('accessToken'); setIsLoggedIn(false); setToken(''); setUserEmail(''); }}
                        style={{marginLeft:'auto',background:'none',border:'none',color:'#94a3b8',cursor:'pointer',fontSize:'.72rem',fontFamily:'inherit'}}>Salir</button>
                    </div>
                    <button onClick={confirmarInscripcion} disabled={sending}
                      style={{width:'100%',padding:'14px',borderRadius:12,border:'none',background:sending?'#93c5fd':'#2563eb',color:'white',fontWeight:700,fontSize:'.95rem',cursor:sending?'not-allowed':'pointer',fontFamily:'inherit',boxShadow:sending?'none':'0 4px 12px rgba(37,99,235,.3)',transition:'all .2s',marginBottom:10}}>
                      {sending ? '⏳ Procesando...' : selectedEvent.price&&Number(selectedEvent.price)>0 ? `💳 Pagar ${(Number(selectedEvent.price)*numPersonas).toFixed(2)}€` : `✅ Confirmar inscripción${numPersonas>1?` (${numPersonas} personas)`:''}`}
                    </button>
                    <button onClick={() => setSelectedEvent(null)} disabled={sending}
                      style={{width:'100%',padding:'11px',borderRadius:12,border:'1px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',color:'#475569'}}>
                      Cancelar
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.80rem',color:'#1d4ed8',display:'flex',gap:8,alignItems:'center'}}>
                      <span>🔐</span> Necesitas una cuenta gratuita para inscribirte
                    </div>
                    <div className="auth-tabs">
                      <button className="auth-tab" onClick={() => setAuthMode('login')} style={{background:authMode==='login'?'white':'transparent',color:authMode==='login'?'#1e293b':'#64748b',boxShadow:authMode==='login'?'0 1px 3px rgba(0,0,0,.1)':'none'}}>Iniciar sesión</button>
                      <button className="auth-tab" onClick={() => setAuthMode('register')} style={{background:authMode==='register'?'white':'transparent',color:authMode==='register'?'#1e293b':'#64748b',boxShadow:authMode==='register'?'0 1px 3px rgba(0,0,0,.1)':'none'}}>Crear cuenta</button>
                    </div>
                    {authMode === 'register' && (
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <input className="fi" placeholder="Nombre *" value={firstName} onChange={e => setFirstName(e.target.value)}/>
                        <input className="fi" placeholder="Apellidos *" value={lastName} onChange={e => setLastName(e.target.value)}/>
                      </div>
                    )}
                    <input className="fi" type="email" placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)}/>
                    <input className="fi" type="password" placeholder="Contraseña *" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key==='Enter' && handleAuth()}/>
                    {authMode === 'register' && <input className="fi" placeholder="Teléfono (opcional)" value={phone} onChange={e => setPhone(e.target.value)}/>}
                    <button onClick={handleAuth} disabled={authLoading}
                      style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:authLoading?'#93c5fd':'#2563eb',color:'white',fontWeight:700,fontSize:'.9rem',cursor:authLoading?'not-allowed':'pointer',fontFamily:'inherit',marginTop:4,marginBottom:8,boxShadow:'0 4px 12px rgba(37,99,235,.25)'}}>
                      {authLoading ? '⏳ Procesando...' : authMode==='login' ? '🔑 Entrar e inscribirme' : '🚀 Registrarme e inscribirme'}
                    </button>
                    <button onClick={() => setSelectedEvent(null)} style={{width:'100%',padding:'11px',borderRadius:12,border:'1px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',color:'#475569'}}>
                      Cancelar
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
